import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import './ResourceAllocationBoardScreen.css'
import {
  useGetAllocationBoardQuery,
  useGetUnassignedTasksQuery,
  useAssignTaskMutation,
  useGetJobPoolQuery,
  useAssignJobMutation,
  useUpdateJobAllocationMutation,
  useGetEmployeeAvailabilityForRangeQuery,
  useUpdateTaskAllocationMutation,
  useGetProjectOptionsQuery,
  useRecalculateBoardAvailabilityMutation,
  useRefreshEmployeeAvailabilityMutation,
} from '../../api/serverStateHooks'
import AppCombobox from '../../components/AppCombobox'
import AllocationAssignmentModal from './components/AllocationAssignmentModal'
import JobAssignmentModal from './components/JobAssignmentModal'
import TaskPoolPanel from './components/TaskPoolPanel'
import JobPoolPanel from './components/JobPoolPanel'
import DayNavBar from './components/DayNavBar'
import DailyTimelineHeader from './components/DailyTimelineHeader'
import DailyTimelineRow from './components/DailyTimelineRow'
import WorkloadProfileFooter from './components/WorkloadProfileFooter'
import {
  WEEKDAYS,
  COL_W,
  ROW_PAD,
  BLOCK_H,
  LANE_GAP,
  MIN_ROW_H,
  DAY_START_HOUR,
  DAY_END_HOUR,
  HOUR_SLOT_W,
  buildDayHours,
  dailyBlockStyle,
  formatDayLabel,
  addDays,
  getDayCapacityLabel,
  getNextAvailableDate,
  parseNaiveDate,
  toDateKey,
  getWeekStartMonday,
  buildWeekDays,
  formatWeekLabel,
  parseDate,
  formatMonthLabel,
  buildMonthDays,
  buildCalendarMatrix,
  getCurrentMonth,
  priorityBadgeColor,
  getTaskPriorityColorClass,
  getSuggestedSlotColorClass,
  getJobStatusColorClass,
  findSuggestedRange,
  findSuggestedRangeBackward,
  blockStyle,
  assignLanes,
  computeDayFreeSlots,
} from './allocationUtils'

// ─────────────────────────────────────────────────────────────────────────────
// Main Board
// ─────────────────────────────────────────────────────────────────────────────
export default function ResourceAllocationBoardScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const modeParam = searchParams.get('mode')
  // 'real' = Real Availability (Committed) Based on Jobs
  // 'estimated' = Estimated Capacity (Forecast) Based on Tasks
  const [boardMode, setBoardMode] = useState(() => (modeParam === 'estimated' ? 'estimated' : 'real'))

  useEffect(() => {
    if (modeParam === 'estimated' || modeParam === 'real') {
      setBoardMode(modeParam)
    }
  }, [modeParam])

  const handleBoardModeChange = useCallback((mode) => {
    setBoardMode(mode)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('mode', mode)
      return next
    })
  }, [setSearchParams])

  const [viewMode, setViewMode] = useState('monthly')
  const [month, setMonth] = useState(getCurrentMonth())
  const [weekStart, setWeekStart] = useState(() => getWeekStartMonday(new Date()))
  const [year, mon] = month.split('-').map(Number)
  const [projectFilter, setProjectFilter] = useState('')

  // Daily timeline state (Real Availability / Jobs mode)
  const [selectedDay, setSelectedDay] = useState(() => new Date())
  const selectedDayStr = useMemo(() => toDateKey(selectedDay), [selectedDay])

  const handlePrevDay = useCallback(() => setSelectedDay((d) => addDays(d, -1)), [])
  const handleNextDay = useCallback(() => setSelectedDay((d) => addDays(d, 1)), [])
  const handleToday = useCallback(() => setSelectedDay(new Date()), [])

  const dayHours = useMemo(() => buildDayHours(DAY_START_HOUR, DAY_END_HOUR), [])

  // Task assignment state (Estimated Capacity mode)
  const [activeCard, setActiveCard] = useState(null)
  const [assignWarnings, setAssignWarnings] = useState([])
  const [draggedTask, setDraggedTask] = useState(null)

  // Job assignment state (Real Availability mode)
  const [activeJobCard, setActiveJobCard] = useState(null)
  const [jobAssignWarnings, setJobAssignWarnings] = useState([])
  const [draggedJob, setDraggedJob] = useState(null)
  const dragJobRef = useRef(null)

  const [blinkTarget, setBlinkTarget] = useState(null)
  const [pendingScroll, setPendingScroll] = useState(null)

  const queryParams = useMemo(() => {
    if (boardMode === 'real') {
      return {
        start_date: selectedDayStr,
        end_date: selectedDayStr,
        view_mode: 'daily',
      }
    }
    if (viewMode === 'weekly') {
      const startStr = toDateKey(weekStart)
      const end = new Date(weekStart)
      end.setDate(weekStart.getDate() + 6)
      const endStr = toDateKey(end)
      return {
        start_date: startStr,
        end_date: endStr,
        view_mode: 'weekly',
      }
    } else {
      return { month }
    }
  }, [boardMode, selectedDayStr, viewMode, weekStart, month])

  // RTK queries & mutations
  const { data: boardData, isLoading: boardLoading, isError: boardError, refetch: refetchBoard } =
    useGetAllocationBoardQuery(queryParams)
  const { data: poolData, isLoading: poolLoading } =
    useGetUnassignedTasksQuery()
  const { data: jobPoolData, isLoading: jobPoolLoading } =
    useGetJobPoolQuery()

  const [assignTask, { isLoading: isAssigning }] = useAssignTaskMutation()
  const [updateTaskAllocation, { isLoading: isUpdating }] = useUpdateTaskAllocationMutation()
  const [assignJob, { isLoading: isJobAssigning }] = useAssignJobMutation()
  const [updateJobAllocation, { isLoading: isJobUpdating }] = useUpdateJobAllocationMutation()
  const [recalculateBoard, { isLoading: isRecalculating }] = useRecalculateBoardAvailabilityMutation()
  const [refreshEmployee] = useRefreshEmployeeAvailabilityMutation()
  const [employeeRefreshingId, setEmployeeRefreshingId] = useState(null)

  // Project options for filter
  const { data: projectOptions = [] } = useGetProjectOptionsQuery({ page: 1, page_size: 200 })
  const projectComboItems = useMemo(
    () => projectOptions.map((p) => ({ value: String(p.id), label: p.name || p.label || String(p.id) })),
    [projectOptions]
  )

  const allEmployees = boardData?.employees || []
  const allTaskPool = poolData?.tasks || []
  const allJobPool = jobPoolData?.jobs || []

  // ── Project-filtered employees, task pool, and job pool ────────────────────────
  const employees = useMemo(() => {
    if (!projectFilter) return allEmployees
    return allEmployees.filter((emp) => {
      const hasMatchingTask = (emp.task_blocks || []).some(
        (b) => String(b.project_id) === String(projectFilter)
      )
      const hasMatchingJob = (emp.job_blocks || []).some(
        (b) => String(b.project_id) === String(projectFilter)
      )
      return hasMatchingTask || hasMatchingJob
    })
  }, [allEmployees, projectFilter])

  const taskPool = useMemo(() => {
    if (!projectFilter) return allTaskPool
    return allTaskPool.filter((t) => String(t.project_id) === String(projectFilter))
  }, [allTaskPool, projectFilter])

  const jobPool = useMemo(() => {
    if (!projectFilter) return allJobPool
    return allJobPool.filter((j) => String(j.project_id) === String(projectFilter))
  }, [allJobPool, projectFilter])

  const monthLabel = formatMonthLabel(year, mon)

  // Build the days to display on the timeline grid (monthly or weekly)
  const timelineDays = useMemo(() => {
    if (viewMode === 'weekly') {
      return buildWeekDays(weekStart)
    }
    return buildMonthDays(year, mon)
  }, [viewMode, weekStart, year, mon])

  // ── Synchronized scroll refs ───────────────────────────────────────────────
  const leftScrollRef = useRef(null)   // Left panel rows viewport
  const rightScrollRef = useRef(null)   // Right panel timeline rows viewport
  const rightHeaderRef = useRef(null)   // Right panel header viewport
  const rightFooterRef = useRef(null)   // Right panel workload footer viewport
  const scrollSource = useRef(null)   // Mutual scroll lock source

  const handleLeftScroll = useCallback(() => {
    if (scrollSource.current === 'right') return
    scrollSource.current = 'left'
    if (rightScrollRef.current && leftScrollRef.current) {
      rightScrollRef.current.scrollTop = leftScrollRef.current.scrollTop
    }
    requestAnimationFrame(() => {
      scrollSource.current = null
    })
  }, [])

  const handleRightScroll = useCallback(() => {
    if (scrollSource.current === 'left') return
    scrollSource.current = 'right'
    if (rightScrollRef.current) {
      const { scrollTop, scrollLeft } = rightScrollRef.current
      if (leftScrollRef.current) {
        leftScrollRef.current.scrollTop = scrollTop
      }
      if (rightHeaderRef.current) {
        rightHeaderRef.current.scrollLeft = scrollLeft
      }
      if (rightFooterRef.current) {
        rightFooterRef.current.scrollLeft = scrollLeft
      }
    }
    requestAnimationFrame(() => {
      scrollSource.current = null
    })
  }, [])

  // ── Scroll to today ────────────────────────────────────────────────────────
  // Centers the timeline viewport on today's column if visible.
  const scrollToToday = useCallback(() => {
    const now = new Date()
    const nowYear = now.getFullYear()
    const nowMon = now.getMonth() + 1

    if (boardMode === 'real') {
      const isToday = toDateKey(selectedDay) === toDateKey(now)
      const targetHour = isToday ? Math.max(0, now.getHours() - 1) : 8
      const targetLeft = Math.max(0, targetHour * HOUR_SLOT_W)
      if (rightScrollRef.current) rightScrollRef.current.scrollLeft = targetLeft
      if (rightHeaderRef.current) rightHeaderRef.current.scrollLeft = targetLeft
      if (rightFooterRef.current) rightFooterRef.current.scrollLeft = targetLeft
      return
    }

    let targetIndex = -1
    if (viewMode === 'weekly') {
      targetIndex = timelineDays.findIndex(d => toDateKey(d.date) === toDateKey(now))
    } else {
      if (nowYear !== year || nowMon !== mon) return
      targetIndex = now.getDate() - 1 // 1-indexed date to 0-indexed column
    }

    if (targetIndex === -1) return
    const vpWidth = rightScrollRef.current ? rightScrollRef.current.clientWidth : 0
    const targetLeft = Math.max(0, targetIndex * COL_W - (vpWidth / 2 - COL_W / 2))
    if (rightScrollRef.current) rightScrollRef.current.scrollLeft = targetLeft
    if (rightHeaderRef.current) rightHeaderRef.current.scrollLeft = targetLeft
    if (rightFooterRef.current) rightFooterRef.current.scrollLeft = targetLeft
  }, [boardMode, selectedDay, viewMode, timelineDays, year, mon])

  // Quick jump to current time in Real Availability (24h) mode
  const scrollToCurrentTime = useCallback(() => {
    const now = new Date()
    const isToday = toDateKey(selectedDay) === toDateKey(now)
    if (!isToday) {
      setSelectedDay(now)
    }
    const nowHourFloat = now.getHours() + now.getMinutes() / 60
    const targetHour = Math.max(0, nowHourFloat - 1)
    const targetLeft = Math.max(0, targetHour * HOUR_SLOT_W)
    requestAnimationFrame(() => {
      if (rightScrollRef.current) rightScrollRef.current.scrollTo({ left: targetLeft, behavior: 'smooth' })
      if (rightHeaderRef.current) rightHeaderRef.current.scrollLeft = targetLeft
      if (rightFooterRef.current) rightFooterRef.current.scrollLeft = targetLeft
    })
  }, [selectedDay])

  // Auto-scroll to today/work hours after board finishes loading
  useEffect(() => {
    if (!boardLoading && !boardError) {
      const id = requestAnimationFrame(() => scrollToToday())
      return () => cancelAnimationFrame(id)
    }
  }, [boardLoading, boardError, selectedDayStr, scrollToToday])

  const executeScrollAndBlink = useCallback((employeeId, targetDateStr, index) => {
    const vpWidth = rightScrollRef.current ? rightScrollRef.current.clientWidth : 0
    const targetLeft = Math.max(0, index * COL_W - (vpWidth / 2 - COL_W / 2))
    if (rightScrollRef.current) rightScrollRef.current.scrollLeft = targetLeft
    if (rightHeaderRef.current) rightHeaderRef.current.scrollLeft = targetLeft
    if (rightFooterRef.current) rightFooterRef.current.scrollLeft = targetLeft

    setBlinkTarget({ employeeId, date: targetDateStr })
    const timer = setTimeout(() => {
      setBlinkTarget(null)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  const handleNextAvailableDateClick = useCallback((employeeId, targetDateStr) => {
    if (!targetDateStr) return
    if (boardMode === 'real') {
      const [y, m, d] = targetDateStr.split('-').map(Number)
      const targetDate = new Date(y, m - 1, d)
      setSelectedDay(targetDate)
      setBlinkTarget({ employeeId, date: targetDateStr })
      const timer = setTimeout(() => {
        setBlinkTarget(null)
      }, 2500)
      return () => clearTimeout(timer)
    }
    const targetDate = new Date(targetDateStr + 'T00:00:00')
    const targetKey = toDateKey(targetDate)
    const targetIndex = timelineDays.findIndex(d => toDateKey(d.date) === targetKey)

    if (targetIndex === -1) {
      if (viewMode === 'weekly') {
        setWeekStart(getWeekStartMonday(targetDate))
      } else {
        const y = targetDate.getFullYear()
        const m = String(targetDate.getMonth() + 1).padStart(2, '0')
        setMonth(`${y}-${m}`)
      }
      setPendingScroll({ employeeId, date: targetKey })
    } else {
      executeScrollAndBlink(employeeId, targetKey, targetIndex)
    }
  }, [boardMode, timelineDays, viewMode, executeScrollAndBlink])

  useEffect(() => {
    if (!pendingScroll || timelineDays.length === 0) return
    const targetIndex = timelineDays.findIndex(d => toDateKey(d.date) === pendingScroll.date)
    if (targetIndex !== -1) {
      executeScrollAndBlink(pendingScroll.employeeId, pendingScroll.date, targetIndex)
      setPendingScroll(null)
    }
  }, [timelineDays, pendingScroll, executeScrollAndBlink])


  // Returns: Map<employee_id, { lanes, rowH, blocks }>
  const employeeLaneMap = useMemo(() => {
    const map = new Map()
    employees.forEach((emp) => {
      if (boardMode === 'real') {
        // Mode 1: Real Availability (Committed) — Based on Jobs for selectedDayStr
        const dayJobs = (emp.job_blocks || []).filter((b) => {
          if (!b.planned_start) return false
          const sDate = toDateKey(parseNaiveDate(b.planned_start))
          const eDate = toDateKey(b.planned_end ? parseNaiveDate(b.planned_end) : parseNaiveDate(b.planned_start))
          return selectedDayStr >= sDate && selectedDayStr <= eDate
        })
        const jobBlocks = dayJobs.map((b) => {
          const s = parseNaiveDate(b.planned_start)
          const e = b.planned_end ? parseNaiveDate(b.planned_end) : new Date(s.getTime() + 60 * 60 * 1000)
          return {
            ...b,
            kind: 'job',
            start: s,
            end: e,
          }
        })
        const slotBlocks = computeDayFreeSlots(emp, selectedDayStr, dayJobs)
        const allBlocks = [...slotBlocks, ...jobBlocks]
        const { lanes, blocks } = assignLanes(allBlocks)
        const rowH = Math.max(MIN_ROW_H, lanes * LANE_GAP + ROW_PAD * 2)
        map.set(emp.employee_id, { lanes, rowH, blocks })
      } else {
        // Mode 2: Estimated Capacity (Forecast) — Based on Tasks
        const taskBlocks = (emp.task_blocks || []).map((b) => ({
          ...b,
          kind: 'task',
          start: parseDate(b.planned_start),
          end: parseDate(b.planned_end || b.planned_start),
        }))
        const slot = emp.available_slot
        const slotBlocks = slot && slot.free_hours > 0 ? [{
          ...slot,
          kind: 'slot',
          start: parseDate(slot.planned_start),
          end: parseDate(slot.planned_end),
        }] : []

        const suggestedBlocks = []
        if (draggedTask && slot && slot.daily_rows && slot.daily_rows.length > 0) {
          const effort = draggedTask.effort_estimate || 8.0
          const priority = String(draggedTask.priority || 'MEDIUM').toUpperCase()
          const todayStr = toDateKey(new Date())
          // All days with any free hours from today onwards
          const activeDays = slot.daily_rows.filter(
            (r) => parseFloat(r.free_hours) > 0 && r.date >= todayStr
          )
          const N = activeDays.length
          if (N > 0) {
            let sStart = null
            let sEnd = null

            if (priority === 'HIGH' || priority === 'URGENT' || priority === 'CRITICAL') {
              // HIGH: scan forward from today — accepts any free capacity, no threshold
              const res = findSuggestedRange(activeDays, effort, 0)
              if (res.success) { sStart = res.start; sEnd = res.end }

            } else if (priority === 'LOW') {
              // LOW: scan backward from end — accepts any free capacity, no threshold
              const limitEndStr = viewMode === 'weekly'
                ? toDateKey(new Date(weekStart.getTime() + 6 * 24 * 3600 * 1000))
                : (() => {
                  const monthEndDayNum = new Date(year, mon, 0).getDate()
                  return `${year}-${String(mon).padStart(2, '0')}-${String(monthEndDayNum).padStart(2, '0')}`
                })()
              const lowPriorityDays = activeDays.filter((r) => r.date <= limitEndStr)
              const res = findSuggestedRangeBackward(lowPriorityDays, effort, 0)
              if (res.success) { sStart = res.start; sEnd = res.end }

            } else {
              // MEDIUM: scan forward starting from 1/3 into the active days — accepts any free capacity, no threshold
              const S = Math.min(N - 1, Math.floor(N / 3))
              const candidates = activeDays.slice(S)
              const res = findSuggestedRange(candidates, effort, 0)
              if (res.success) { sStart = res.start; sEnd = res.end }
            }

            if (sStart && sEnd) {
              suggestedBlocks.push({
                kind: 'suggested',
                planned_start: sStart,
                planned_end: sEnd,
                start: parseDate(sStart),
                end: parseDate(sEnd),
                priority: priority,
                effort_estimate: effort,
                task_name: draggedTask.task_name || draggedTask.name || '',
              })
            }
          }
        }

        const all = [...slotBlocks, ...taskBlocks, ...suggestedBlocks]
        const { lanes, blocks } = assignLanes(all)
        const rowH = Math.max(MIN_ROW_H, lanes * LANE_GAP + ROW_PAD * 2)
        map.set(emp.employee_id, { lanes, rowH, blocks })
      }
    })
    return map
  }, [employees, boardMode, selectedDayStr, draggedTask, viewMode, weekStart, year, mon])

  // Auto-scroll to the earliest suggested slot whenever a drag begins.
  useEffect(() => {
    if (!draggedTask) return
    let earliestDateKey = null
    for (const [, layout] of employeeLaneMap.entries()) {
      const suggested = layout.blocks.find((b) => b.kind === 'suggested')
      if (suggested && suggested.planned_start) {
        if (!earliestDateKey || suggested.planned_start < earliestDateKey) {
          earliestDateKey = suggested.planned_start
        }
      }
    }
    if (!earliestDateKey) return
    const idx = timelineDays.findIndex((dayObj) => toDateKey(dayObj.date) === earliestDateKey)
    if (idx === -1) return
    requestAnimationFrame(() => {
      const vpWidth = rightScrollRef.current ? rightScrollRef.current.clientWidth : 0
      const targetLeft = Math.max(0, idx * COL_W - (vpWidth / 2 - COL_W / 2))
      if (rightScrollRef.current) rightScrollRef.current.scrollLeft = targetLeft
      if (rightHeaderRef.current) rightHeaderRef.current.scrollLeft = targetLeft
      if (rightFooterRef.current) rightFooterRef.current.scrollLeft = targetLeft
    })
  }, [draggedTask, employeeLaneMap, timelineDays])

  // ── Availability refresh handlers ──────────────────────────────────────────
  // Bulk: recalculate availability for ALL currently visible employees.
  async function handleBoardRefresh() {
    const ids = employees.map((e) => e.employee_id)
    if (!ids.length) return
    try {
      if (boardMode === 'real') {
        await recalculateBoard({ start_date: selectedDayStr, end_date: selectedDayStr, employee_ids: ids }).unwrap()
      } else if (viewMode === 'weekly') {
        const startStr = toDateKey(weekStart)
        const end = new Date(weekStart)
        end.setDate(weekStart.getDate() + 6)
        const endStr = toDateKey(end)
        await recalculateBoard({ start_date: startStr, end_date: endStr, employee_ids: ids }).unwrap()
      } else {
        await recalculateBoard({ month, employee_ids: ids }).unwrap()
      }
    } catch (_) { /* errors are surfaced via RTK query invalidation */ }
  }

  // Per-employee: refresh one resource availability.
  async function handleEmployeeRefresh(employeeId) {
    let startStr, endStr
    if (boardMode === 'real') {
      startStr = selectedDayStr
      endStr = selectedDayStr
    } else if (viewMode === 'weekly') {
      startStr = toDateKey(weekStart)
      const end = new Date(weekStart)
      end.setDate(weekStart.getDate() + 6)
      endStr = toDateKey(end)
    } else {
      const [y, m] = month.split('-').map(Number)
      startStr = `${y}-${String(m).padStart(2, '0')}-01`
      const daysInMonth = new Date(y, m, 0).getDate()
      endStr = `${y}-${String(m).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`
    }
    setEmployeeRefreshingId(employeeId)
    try {
      await refreshEmployee({ employee_id: employeeId, start_date: startStr, end_date: endStr }).unwrap()
    } finally {
      setEmployeeRefreshingId(null)
    }
  }

  // Month navigation
  function moveMonth(dir) {
    const d = new Date(year, mon - 1 + dir, 1)
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  function moveWeek(dir) {
    setWeekStart((prev) => {
      const next = new Date(prev)
      next.setDate(prev.getDate() + dir * 7)
      return next
    })
  }

  function handleViewModeChange(mode) {
    setViewMode(mode)
    if (mode === 'weekly') {
      const [y, m] = month.split('-').map(Number)
      const now = new Date()
      if (now.getFullYear() === y && (now.getMonth() + 1) === m) {
        setWeekStart(getWeekStartMonday(now))
      } else {
        setWeekStart(getWeekStartMonday(new Date(y, m - 1, 1)))
      }
    } else {
      const d = new Date(weekStart)
      setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    }
  }


  // Drag-and-drop
  const dragTaskRef = useRef(null)

  function handleDragStart(task) {
    dragTaskRef.current = task
    setDraggedTask(task)
  }

  function handleDragEnd() {
    dragTaskRef.current = null
    setDraggedTask(null)
  }

  function handleDropOnSlot(employee) {
    const task = dragTaskRef.current

    // Find if there's a suggested slot for this employee BEFORE clearing draggedTask
    const layout = employeeLaneMap.get(employee.employee_id)
    const suggestedSlot = layout?.blocks.find((b) => b.kind === 'suggested')

    dragTaskRef.current = null
    setDraggedTask(null)
    setAssignWarnings([])
    setActiveCard({
      employee,
      preTask: task || null,
      isUpdate: false,
      suggestedDates: suggestedSlot ? { start: suggestedSlot.planned_start, end: suggestedSlot.planned_end } : null
    })
  }

  function handleSlotClick(employee) {
    setAssignWarnings([])
    setActiveCard({ employee, preTask: null, isUpdate: false })
  }

  function handleTaskBlockClick(employee, block) {
    setAssignWarnings([])
    setActiveCard({ employee, preTask: block, isUpdate: true })
  }

  async function handleAssign(payload) {
    const { isUpdate: isUpd, ignore_warnings, ...apiPayload } = payload
    try {
      let result
      if (isUpd) {
        result = await updateTaskAllocation({ ...apiPayload, ignore_warnings }).unwrap()
      } else {
        result = await assignTask({ ...apiPayload, ignore_warnings }).unwrap()
      }
      const warnings = result?.warnings || []
      const needsConfirm = result?.requires_confirm === true
      if (warnings.length > 0 && needsConfirm) {
        // Backend says: show warning and wait for manager to confirm
        setAssignWarnings(warnings)
        // Do NOT close the modal — user needs to click Confirm & Proceed
        return
      }
      setActiveCard(null)
      setAssignWarnings([])
      refetchBoard()
    } catch (err) {
      const detail = err?.data?.detail || 'Operation failed'
      setAssignWarnings([String(detail)])
    }
  }

  // Job drag-and-drop & allocation handlers
  function handleJobDragStart(job) {
    dragJobRef.current = job
    setDraggedJob(job)
  }

  function handleJobDragEnd() {
    dragJobRef.current = null
    setDraggedJob(null)
  }

  function handleDropJobOnSlot(employee) {
    const job = dragJobRef.current
    dragJobRef.current = null
    setDraggedJob(null)
    setJobAssignWarnings([])
    setActiveJobCard({
      employee,
      job: job || null,
      isUpdate: false,
    })
  }

  function handleJobSlotClick(employee, slot) {
    setJobAssignWarnings([])
    setActiveJobCard({
      employee,
      job: null,
      isUpdate: false,
      slotTimes: { startTime: slot.startTime, endTime: slot.endTime, hours: slot.free_hours },
    })
  }

  function handleDropJobOnSlotBlock(employee, slot) {
    const job = dragJobRef.current
    dragJobRef.current = null
    setDraggedJob(null)
    setJobAssignWarnings([])
    setActiveJobCard({
      employee,
      job: job || null,
      isUpdate: false,
      slotTimes: { startTime: slot.startTime, endTime: slot.endTime, hours: slot.free_hours },
    })
  }

  function handleJobBlockClick(employee, block) {
    setJobAssignWarnings([])
    setActiveJobCard({
      employee,
      job: block,
      isUpdate: true,
    })
  }

  async function handleJobAssign(payload) {
    const { isUpdate: isUpd, ignore_warnings, ...apiPayload } = payload
    try {
      let result
      if (isUpd) {
        result = await updateJobAllocation({ ...apiPayload, ignore_warnings }).unwrap()
      } else {
        result = await assignJob({ ...apiPayload, ignore_warnings }).unwrap()
      }
      const warnings = result?.warnings || []
      const needsConfirm = result?.requires_confirm === true
      if (warnings.length > 0 && needsConfirm) {
        setJobAssignWarnings(warnings)
        return
      }
      setActiveJobCard(null)
      setJobAssignWarnings([])
      refetchBoard()
    } catch (err) {
      const detail = err?.data?.detail || 'Operation failed'
      setJobAssignWarnings([String(detail)])
    }
  }

  return (
    <div className="flex flex-col gap-6 w-full pb-16 font-body text-on-surface">

      {/* Header */}
      <section className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6">
        <div className="max-w-xl">
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-on-surface mb-2 leading-none">
            Resource Allocation
          </h2>
          <p className="font-body text-on-surface-variant text-base">
            Strategic workforce management and workload mapping.
          </p>
        </div>

        {/* Dual Mode Switcher + Project Filter */}
        <div className="flex flex-wrap items-end gap-4 w-full xl:w-auto">
          {/* Dual Mode Toggle */}
          <div className="flex flex-col">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-outline mb-1.5">
              Allocation Mode
            </label>
            <div className="inline-flex items-center bg-surface-container-low p-1 rounded-2xl border border-outline-variant/30 shadow-inner">
              <button
                type="button"
                onClick={() => handleBoardModeChange('real')}
                className={clsx(
                  'flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-left transition-all cursor-pointer',
                  boardMode === 'real'
                    ? 'bg-primary text-on-primary shadow-sm font-bold'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high/50'
                )}
              >
                <span className="material-symbols-outlined text-base">
                  {boardMode === 'real' ? 'radio_button_checked' : 'radio_button_unchecked'}
                </span>
                <div className="flex flex-col">
                  <span className="text-xs font-bold leading-tight">Real Availability (Committed)</span>
                  <span className={clsx('text-[10px] leading-tight font-medium', boardMode === 'real' ? 'text-on-primary/80' : 'text-outline')}>
                    Based on Jobs
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleBoardModeChange('estimated')}
                className={clsx(
                  'flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-left transition-all cursor-pointer',
                  boardMode === 'estimated'
                    ? 'bg-primary text-on-primary shadow-sm font-bold'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high/50'
                )}
              >
                <span className="material-symbols-outlined text-base">
                  {boardMode === 'estimated' ? 'radio_button_checked' : 'radio_button_unchecked'}
                </span>
                <div className="flex flex-col">
                  <span className="text-xs font-bold leading-tight">Estimated Capacity (Forecast)</span>
                  <span className={clsx('text-[10px] leading-tight font-medium', boardMode === 'estimated' ? 'text-on-primary/80' : 'text-outline')}>
                    Based on Tasks
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Project Filter */}
          <div className="shrink-0 min-w-[200px]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-outline mb-1.5">
              Filter by Project
            </label>
            <AppCombobox
              items={projectComboItems}
              value={projectFilter}
              onChange={setProjectFilter}
              placeholder="All Projects"
              searchPlaceholder="Search projects..."
              allowClear={true}
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* Board + Task Pool */}
      <div className="grid grid-cols-12 gap-6 items-stretch">

        {/* ── Gantt Board (Col 1-9) ── */}
        <div className="col-span-12 lg:col-span-9 flex flex-col h-full">
          <div className="overflow-hidden rounded-[22px] border border-outline-variant/20 bg-surface-container-lowest flex flex-col h-full shadow-sm">

            {/* Toolbar */}
            <div className="p-3.5 sm:px-5 sm:py-3 border-b border-outline-variant/15 flex flex-wrap justify-between items-center gap-3 bg-surface-container-lowest/70 backdrop-blur-xs shrink-0">
              {boardMode === 'real' ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <DayNavBar
                    selectedDay={selectedDay}
                    onPrev={handlePrevDay}
                    onToday={handleToday}
                    onNext={handleNextDay}
                    onDayChange={setSelectedDay}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-xl border border-outline-variant/30 bg-surface p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => (viewMode === 'weekly' ? moveWeek(-1) : moveMonth(-1))}
                      className="p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                      title={viewMode === 'weekly' ? 'Previous Week' : 'Previous Month'}
                    >
                      <span className="material-symbols-outlined text-base leading-none">chevron_left</span>
                    </button>
                    <div className="w-px h-3.5 bg-outline-variant/30 my-auto" />
                    <button
                      type="button"
                      onClick={() => (viewMode === 'weekly' ? moveWeek(1) : moveMonth(1))}
                      className="p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                      title={viewMode === 'weekly' ? 'Next Week' : 'Next Month'}
                    >
                      <span className="material-symbols-outlined text-base leading-none">chevron_right</span>
                    </button>
                  </div>

                  <div className="h-5 w-px bg-outline-variant/30 hidden sm:block" />

                  <span className="text-xs sm:text-sm font-bold text-on-surface tracking-tight">
                    {viewMode === 'weekly' ? formatWeekLabel(weekStart) : monthLabel}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                {/* Real mode: quick jump to current time */}
                {boardMode === 'real' && (
                  <button
                    type="button"
                    onClick={scrollToCurrentTime}
                    title="Scroll timeline to current time"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-surface hover:bg-surface-container-high text-on-surface-variant hover:text-primary rounded-xl font-bold text-xs transition-all border border-outline-variant/30 shadow-2xs cursor-pointer active:scale-95"
                  >
                    <span className="material-symbols-outlined text-sm text-primary">schedule</span>
                    <span className="hidden sm:inline">Current Time</span>
                  </button>
                )}

                {/* Monthly/Weekly Switcher only shown in Estimated mode */}
                {boardMode === 'estimated' && (
                  <div className="flex overflow-hidden rounded-xl border border-outline-variant/30 bg-surface p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => handleViewModeChange('monthly')}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer',
                        viewMode === 'monthly' ? 'bg-primary text-on-primary shadow-xs' : 'text-on-surface-variant hover:text-primary'
                      )}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewModeChange('weekly')}
                      className={clsx(
                        'px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer',
                        viewMode === 'weekly' ? 'bg-primary text-on-primary shadow-xs' : 'text-on-surface-variant hover:text-primary'
                      )}
                    >
                      Weekly
                    </button>
                  </div>
                )}

                {/* Today button for estimated mode */}
                {boardMode === 'estimated' && (
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date()
                      if (viewMode === 'weekly') {
                        setWeekStart(getWeekStartMonday(today))
                      } else {
                        const currentM = getCurrentMonth()
                        if (currentM === month) {
                          requestAnimationFrame(() => scrollToToday())
                        } else {
                          setMonth(currentM)
                        }
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-surface hover:bg-surface-container-high text-on-surface-variant hover:text-primary rounded-xl font-bold text-xs transition-all active:scale-95 border border-outline-variant/30 shadow-2xs cursor-pointer"
                    title="Jump to current week/month"
                  >
                    <span className="material-symbols-outlined text-sm text-primary">today</span>
                    <span>Today</span>
                  </button>
                )}

                {/* Bulk Refresh Availability */}
                <button
                  type="button"
                  onClick={handleBoardRefresh}
                  disabled={isRecalculating || boardLoading}
                  title={
                    boardMode === 'real'
                      ? "Refresh availability for all visible employees today"
                      : viewMode === 'weekly'
                        ? "Refresh availability for all visible employees this week"
                        : "Refresh availability for all visible employees this month"
                  }
                  className="flex items-center gap-1.5 px-3.5 py-1.5 bg-secondary-container text-on-secondary-container hover:bg-secondary-container/85 rounded-xl font-bold text-xs transition-all active:scale-95 shadow-2xs disabled:opacity-60 cursor-pointer"
                >
                  <span className={clsx('material-symbols-outlined text-sm', isRecalculating && 'animate-spin')}>
                    refresh
                  </span>
                  <span>{isRecalculating ? 'Refreshing...' : 'Refresh'}</span>
                </button>
              </div>
            </div>


            {/* Loading / Error states */}
            {boardLoading && (
              <div className="p-10 text-center text-sm text-on-surface-variant">
                Loading board...
              </div>
            )}
            {boardError && !boardLoading && (
              <div className="px-6 py-4 text-sm text-error bg-error-container/20">
                Failed to load allocation board. Please try again.
              </div>
            )}

            {/* ── Synchronized scroll body ── */}
            {!boardLoading && !boardError && (
              <div className="gantt-board-body flex flex-row w-full overflow-hidden" style={{ height: '600px' }}>

                {/* ── Left Panel (Employee details) ── */}
                <div className="w-72 shrink-0 border-r border-outline-variant/20 bg-surface-container-lowest shadow-[4px_0_12px_-4px_rgba(0,0,0,0.05)] flex flex-col z-20">
                  {/* Fixed Header */}
                  <div className="h-12 border-b border-outline-variant/20 flex items-center px-6 bg-surface-container-low/80 shrink-0">
                    <span className="font-label text-[10px] font-bold uppercase tracking-wider">
                      Resource
                    </span>
                  </div>

                  {/* Scrollable employee rows */}
                  <div
                    ref={leftScrollRef}
                    onScroll={handleLeftScroll}
                    className="gantt-y-axis-scroll gantt-scroll-viewport scrollbar-none"
                  >
                    {employees.map((emp) => {
                      const layout = employeeLaneMap.get(emp.employee_id) || { rowH: MIN_ROW_H }
                      const summary = emp.monthly_summary || {}

                      let base, used, utilized, isOverload
                      if (boardMode === 'real') {
                        base = emp.base_hours_per_day || 8
                        const dayJobs = (emp.job_blocks || []).filter((b) => {
                          if (!b.planned_start) return false
                          const sDate = toDateKey(parseNaiveDate(b.planned_start))
                          const eDate = toDateKey(b.planned_end ? parseNaiveDate(b.planned_end) : parseNaiveDate(b.planned_start))
                          return selectedDayStr >= sDate && selectedDayStr <= eDate
                        })
                        used = dayJobs.reduce((acc, b) => acc + (b.planned_hours || 0), 0)
                        utilized = base > 0 ? Math.round((used / base) * 100) : 0
                        isOverload = utilized > 100
                      } else {
                        base = summary.total_base_hours || 0
                        const free = summary.total_free_hours || 0
                        used = Math.round((summary.total_allocated_hours || 0) + (summary.total_meeting_hours || 0))
                        utilized = base > 0 ? Math.round(((base - free) / base) * 100) : 0
                        isOverload = utilized > 100
                      }

                      return (
                        <div
                          key={emp.employee_id}
                          className="p-4 flex flex-col justify-start bg-surface-container-lowest border-b border-outline-variant/40 shrink-0"
                          style={{ height: `${layout.rowH}px` }}
                        >
                          <div className="flex items-center gap-3 mb-2.5">
                            <div className="w-10 h-10 rounded-full border-2 border-primary-fixed overflow-hidden bg-surface-container flex items-center justify-center shrink-0">
                              <span className="font-bold text-sm text-primary">
                                {String(emp.name || 'U').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-sm leading-tight truncate">{emp.name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <p className="text-[10px] text-on-surface-variant font-medium truncate">
                                  {emp.designation || emp.department || '—'}
                                </p>
                                {boardMode === 'real' && emp.shift_name && (
                                  <span
                                    className="text-[9px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded leading-none shrink-0"
                                    title={`Shift: ${emp.shift_name} (${emp.shift_intime || ''} – ${emp.shift_outtime || ''})`}
                                  >
                                    {emp.shift_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex-1 h-1.5 bg-surface-variant rounded-full overflow-hidden">
                              <div
                                className={clsx(
                                  'h-full rounded-full transition-all duration-500',
                                  isOverload ? 'bg-error' : 'bg-primary',
                                )}
                                style={{ width: `${Math.min(100, utilized)}%` }}
                              />
                            </div>
                            <div className="flex items-center gap-1 ml-2 shrink-0">
                              <span className={clsx(
                                'text-[10px] font-bold',
                                isOverload ? 'text-error' : 'text-primary',
                              )}>
                                {utilized}%
                              </span>
                              {/* Per-employee inline refresh */}
                              <button
                                type="button"
                                onClick={() => handleEmployeeRefresh(emp.employee_id)}
                                disabled={employeeRefreshingId === emp.employee_id}
                                title={`Refresh availability for ${emp.name}`}
                                className="p-0.5 rounded-full text-on-surface-variant hover:text-primary hover:bg-surface-container transition-colors disabled:opacity-50"
                              >
                                <span className={clsx('material-symbols-outlined text-xs', employeeRefreshingId === emp.employee_id && 'animate-spin')}>
                                  refresh
                                </span>
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[9px] text-on-surface-variant font-medium">
                              {used}h used
                            </span>
                            <span className="text-[9px] text-outline font-medium">
                              / {Math.round(base)}h {boardMode === 'real' ? 'day cap' : 'capacity'}
                            </span>
                          </div>
                          {emp.next_available_date ? (
                            <button
                              type="button"
                              onClick={() => handleNextAvailableDateClick(emp.employee_id, emp.next_available_date)}
                              className="w-full mt-2.5 px-3 py-1.5 flex items-center justify-center gap-1.5 rounded-xl border border-emerald-600/30 bg-emerald-50/50 hover:bg-emerald-100/60 text-[10px] font-bold text-emerald-800 transition-all active:scale-[0.98] cursor-pointer"
                              title="Click to jump to next free date"
                            >
                              <span className="material-symbols-outlined text-xs select-none">event_upcoming</span>
                              Next Free: {new Date(emp.next_available_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </button>
                          ) : (
                            <div className="w-full mt-2.5 px-3 py-1.5 flex items-center justify-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low text-[10px] font-bold text-outline select-none">
                              <span className="material-symbols-outlined text-xs">event_busy</span>
                              Fully Booked
                            </div>
                          )}

                        </div>
                      )
                    })}
                  </div>

                  {/* Fixed Footer */}
                  <div className="border-t border-outline-variant/20 flex flex-col justify-center px-6 h-20 bg-surface-container-low/30 shrink-0">
                    <span className="font-label text-[10px] font-bold uppercase tracking-wider text-on-surface">
                      Total Workload Profile
                    </span>
                    <span className="text-[9px] text-outline mt-0.5">
                      {boardMode === 'real' ? 'Hourly committed load' : 'Daily capacity consumption'}
                    </span>
                  </div>
                </div>

                {/* ── Right Panel (Timeline Grid) ── */}
                <div className="flex-1 flex flex-col overflow-hidden bg-surface-container-lowest">
                  {/* Fixed Day / Hour Header: scrolls horizontally in sync */}
                  <div
                    ref={rightHeaderRef}
                    className="h-12 border-b border-outline-variant/20 overflow-hidden shrink-0 scrollbar-none"
                  >
                    {boardMode === 'real' ? (
                      <DailyTimelineHeader
                        hours={dayHours}
                        slotWidth={HOUR_SLOT_W}
                        selectedDay={selectedDay}
                      />
                    ) : (
                      <div style={{ minWidth: `${timelineDays.length * COL_W}px` }} className="flex bg-surface-container-lowest/95 h-full">
                        {timelineDays.map((day) => (
                          <div
                            key={toDateKey(day.date)}
                            className="shrink-0 border-r border-outline-variant/10 flex flex-col items-center justify-center font-label"
                            style={{ width: `${COL_W}px` }}
                          >
                            <span className="text-[10px] font-bold text-on-surface">{day.dayNum}</span>
                            <span className="text-[8px] font-semibold text-outline uppercase mt-0.5">{day.weekday}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Scrollable Timeline Grid: scrolls vertically & horizontally */}
                  <div
                    ref={rightScrollRef}
                    onScroll={handleRightScroll}
                    className="gantt-timeline-scroll gantt-scroll-viewport custom-scrollbar"
                  >
                    {boardMode === 'real' ? (
                      <div
                        style={{
                          width: `${dayHours.length * HOUR_SLOT_W}px`,
                          minWidth: `${dayHours.length * HOUR_SLOT_W}px`,
                        }}
                        className="relative bg-surface-container-lowest"
                      >
                        {employees.map((emp) => {
                          const layout = employeeLaneMap.get(emp.employee_id) || { rowH: MIN_ROW_H, blocks: [] }
                          return (
                            <DailyTimelineRow
                              key={emp.employee_id}
                              employee={emp}
                              blocks={layout.blocks}
                              jobBlocks={emp.job_blocks || []}
                              dateStr={selectedDayStr}
                              hours={dayHours}
                              slotWidth={HOUR_SLOT_W}
                              rowH={layout.rowH}
                              blinkTarget={blinkTarget}
                              onDrop={(targetEmp) => handleDropJobOnSlot(targetEmp)}
                              onSlotDrop={(targetEmp, slot) => handleDropJobOnSlotBlock(targetEmp, slot)}
                              onSlotClick={(targetEmp, slot) => handleJobSlotClick(targetEmp, slot)}
                              onJobClick={(jobBlock, targetEmp) => handleJobBlockClick(targetEmp, jobBlock)}
                              draggedJob={draggedJob}
                            />
                          )
                        })}
                      </div>
                    ) : (
                      <div style={{ minWidth: `${timelineDays.length * COL_W}px` }} className="gantt-grid relative">
                        {employees.map((emp) => {
                          const layout = employeeLaneMap.get(emp.employee_id) || { rowH: MIN_ROW_H, blocks: [] }

                          return (
                            <div
                              key={emp.employee_id}
                              className="gantt-timeline-row"
                              style={{ height: `${layout.rowH}px` }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleDropOnSlot(emp)}
                            >
                              {/* Low-opacity horizontal mid-separators between lanes */}
                              {Array.from({ length: layout.lanes - 1 }, (_, li) => (
                                <div
                                  key={`sep-${li}`}
                                  className="gantt-lane-separator"
                                  style={{ top: `${ROW_PAD + (li + 1) * LANE_GAP}px` }}
                                />
                              ))}

                              {/* Render all blocks in assigned lanes */}
                              {layout.blocks.map((block, bi) => {
                                const timelineStartDate = timelineDays.length > 0
                                  ? timelineDays[0].date
                                  : null
                                const timelineEndDate = timelineDays.length > 0
                                  ? timelineDays[timelineDays.length - 1].date
                                  : null
                                const s = blockStyle(timelineStartDate, timelineEndDate, block.planned_start, block.planned_end)
                                if (!s) return null

                                const topPx = ROW_PAD + block.lane * LANE_GAP

                                // ── Available Slot ──
                                if (block.kind === 'slot') {
                                  return (
                                    <div
                                      key={`slot-${bi}`}
                                      style={{ ...s, top: `${topPx}px`, height: `${BLOCK_H}px` }}
                                      onClick={() => handleSlotClick(emp)}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={(e) => { e.stopPropagation(); handleDropOnSlot(emp) }}
                                      className="gantt-block-slot absolute rounded-xl flex items-center px-4 cursor-pointer group border-2 border-dashed border-primary text-primary transition-colors"
                                    >
                                      <div className="flex flex-col min-w-0">
                                        <span className="text-[11px] font-bold leading-tight truncate text-primary">
                                          Available Capacity
                                        </span>
                                        <span className="text-[9px] font-medium mt-0.5 text-primary/70">
                                          {block.free_hours}h free — click or drop task
                                        </span>
                                      </div>
                                      <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        <span className="material-symbols-outlined text-sm text-primary">add_task</span>
                                      </div>
                                    </div>
                                  )
                                }

                                // ── Suggested Slot ──
                                if (block.kind === 'suggested') {
                                  return (
                                    <div
                                      key={`suggested-${bi}`}
                                      style={{ ...s, top: `${topPx}px`, height: `${BLOCK_H}px` }}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={(e) => { e.stopPropagation(); handleDropOnSlot(emp) }}
                                      className={clsx(
                                        'gantt-block-suggested absolute rounded-xl flex items-center px-4 cursor-pointer group border-2 border-dashed transition-colors animate-blink-3 shadow-sm hover:shadow-md',
                                        getSuggestedSlotColorClass(block.priority)
                                      )}
                                      onClick={() => {
                                        setAssignWarnings([])
                                        setActiveCard({
                                          employee: emp,
                                          preTask: draggedTask,
                                          isUpdate: false,
                                          suggestedDates: { start: block.planned_start, end: block.planned_end }
                                        })
                                      }}
                                    >
                                      <div className="flex flex-col min-w-0">
                                        <span className="text-[10px] font-bold leading-tight truncate">
                                          Suggested: {block.task_name || 'Task'}
                                        </span>
                                        <span className="text-[8px] font-medium mt-0.5 opacity-80 truncate">
                                          {block.priority} · {block.effort_estimate}h — click or drop
                                        </span>
                                      </div>
                                      <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        <span className="material-symbols-outlined text-sm">add_task</span>
                                      </div>
                                    </div>
                                  )
                                }

                                // ── Task block ──
                                if (block.kind === 'task') {
                                  return (
                                    <div
                                      key={`t-${block.task_id}-${bi}`}
                                      style={{ ...s, top: `${topPx}px`, height: `${BLOCK_H}px` }}
                                      onClick={() => handleTaskBlockClick(emp, block)}
                                      className={clsx(
                                        "gantt-block-task absolute h-10 rounded-xl flex items-center px-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer",
                                        getTaskPriorityColorClass(block.priority)
                                      )}
                                    >
                                      <div className="flex flex-col min-w-0">
                                        <span className="text-[10px] font-bold leading-tight truncate">
                                          {block.task_name}
                                        </span>
                                        <span className="text-[8px] font-medium mt-0.5 opacity-80 truncate">
                                          {block.project_name}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                }

                                return null
                              })}
                              {/* Blinking highlight overlay for next available date */}
                              {blinkTarget && blinkTarget.employeeId === emp.employee_id && (
                                (() => {
                                  const timelineStartDate = timelineDays.length > 0 ? timelineDays[0].date : null
                                  timelineStartDate // Keep eslint happy if needed
                                  const timelineEndDate = timelineDays.length > 0 ? timelineDays[timelineDays.length - 1].date : null
                                  const s = blockStyle(timelineStartDate, timelineEndDate, blinkTarget.date, blinkTarget.date)
                                  if (!s) return null
                                  return (
                                    <div
                                      className="absolute animate-blink-3"
                                      style={{
                                        ...s,
                                        top: 0,
                                        height: '100%',
                                        backgroundColor: 'rgba(59, 130, 246, 0.25)',
                                        border: '2px solid rgb(59, 130, 246)',
                                        borderRadius: '8px',
                                        pointerEvents: 'none',
                                        zIndex: 30,
                                      }}
                                    />
                                  )
                                })()
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Fixed Footer: scrolls horizontally in sync */}
                  <div
                    ref={rightFooterRef}
                    className="h-20 border-t border-outline-variant/20 overflow-hidden shrink-0 scrollbar-none"
                  >
                    {boardMode === 'real' ? (
                      <WorkloadProfileFooter
                        employees={employees}
                        dateStr={selectedDayStr}
                        hours={dayHours}
                        slotWidth={HOUR_SLOT_W}
                      />
                    ) : (
                      <div style={{ width: `${timelineDays.length * COL_W}px` }} className="flex bg-surface-container-low/20 h-full items-end">
                        {timelineDays.map((dayObj) => {
                          const dayKey = toDateKey(dayObj.date)
                          const totalBase = employees.reduce((sum, e) => {
                            const day = (e.available_slot?.daily_rows || []).find(r => r.date === dayKey)
                            return sum + (day?.base_hours || e.base_hours_per_day || 8)
                          }, 0)
                          const totalFree = employees.reduce((sum, e) => {
                            const day = (e.available_slot?.daily_rows || []).find(r => r.date === dayKey)
                            return sum + (day?.free_hours ?? e.base_hours_per_day ?? 8)
                          }, 0)
                          const load = totalBase > 0 ? Math.round(((totalBase - totalFree) / totalBase) * 100) : 0
                          const barColor = load > 100 ? 'bg-red-500/20' : load > 80 ? 'bg-amber-500/20' : 'bg-green-500/20'
                          const bgColor = load > 100 ? 'bg-red-500/5' : load > 80 ? 'bg-amber-500/5' : 'bg-green-500/5'
                          return (
                            <div
                              key={dayKey}
                              style={{ width: `${COL_W}px` }}
                              className={clsx('shrink-0 border-r border-outline-variant/5 flex items-end h-full', bgColor)}
                            >
                              <div className={clsx('w-full transition-all duration-500', barColor)} style={{ height: `${Math.min(100, load)}%` }} />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* ── Pool Panel (Col 10-12): Swaps between Job Pool and Task Pool based on Mode ── */}
        <div className="col-span-12 lg:col-span-3 flex flex-col h-full">
          {boardMode === 'real' ? (
            <JobPoolPanel
              jobPool={jobPool}
              isLoading={jobPoolLoading}
              onDragStart={handleJobDragStart}
              onDragEnd={handleJobDragEnd}
            />
          ) : (
            <TaskPoolPanel
              taskPool={taskPool}
              isLoading={poolLoading}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          )}
        </div>

      </div>

      {/* Task Assignment Modal (Estimated Capacity Mode) */}
      {activeCard && (
        <AllocationAssignmentModal
          employee={activeCard.employee}
          preSelectedTask={activeCard.preTask}
          suggestedDates={activeCard.suggestedDates}
          month={month}
          onAssign={handleAssign}
          onClose={() => { setActiveCard(null); setAssignWarnings([]) }}
          isAssigning={isAssigning || isUpdating}
          assignWarnings={assignWarnings}
          isUpdate={activeCard.isUpdate || false}
          onRefresh={() => handleEmployeeRefresh(activeCard.employee.employee_id)}
          isRefreshing={employeeRefreshingId === activeCard.employee.employee_id}
          mode="assign"
        />
      )}

      {/* Job Assignment Modal (Real Availability Mode) */}
      {activeJobCard && (
        <JobAssignmentModal
          employee={activeJobCard.employee}
          job={activeJobCard.job}
          slotTimes={activeJobCard.slotTimes || null}
          jobPool={jobPool}
          month={month}
          initialDate={selectedDayStr}
          onAssign={handleJobAssign}
          onClose={() => { setActiveJobCard(null); setJobAssignWarnings([]) }}
          isAssigning={isJobAssigning || isJobUpdating}
          assignWarnings={jobAssignWarnings}
          isUpdate={activeJobCard.isUpdate || false}
          onRefresh={() => handleEmployeeRefresh(activeJobCard.employee.employee_id)}
          isRefreshing={employeeRefreshingId === activeJobCard.employee.employee_id}
        />
      )}
    </div>
  )
}
